import React, { useState, useMemo, useCallback, useRef } from 'react';
import { DatePicker, Select } from '@tencent/tea-component';
import { useOutsideClick } from '@tencent/tea-component/lib/util';
import moment, { Moment } from 'moment-timezone';
import 'moment/locale/zh-cn';
import {
  setPosition,
  noLessThan,
  getTimeOfNearHalfHour,
  timeSelectOptions,
  addNumberPerfix,
} from './util';
import '@tencent/tpm-meeting-active/lib/Datetimepicker/style';
import classNames from 'classnames';

interface Props {
  start: number;
  end: number;
  endDisabled?: boolean;
  startDisabled?: boolean;
  startDateRange?: [Moment, Moment];
  endDateRange?: [Moment, Moment];
  timeZone?: string;
  className?: string;
  noSplit?: boolean;
  onChange?: (startDate: Moment, endDate: Moment) => void;
}

const prefixCls = 'ma-t-picker';

const DateTimePicker = (props: Props) => {
  const {
    start,
    end,
    endDisabled,
    startDisabled,
    startDateRange,
    endDateRange,
    timeZone = 'Asia/Shanghai',
    noSplit = false,
    className,
    onChange,
  } = props;
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isStartTimeOpen, setIsStartTimeOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);
  const [isEndTimeOpen, setIsEndTimeOpen] = useState(false);
  const startDate = useMemo(() => moment(start, 'X').tz(timeZone), [start, timeZone]);
  const endDate = useMemo(() => moment(end, 'X').tz(timeZone), [end, timeZone]);
  // 之前正确时间
  const [beforeStartDate, setBeforeStartDate] = useState(moment(start, 'X').tz(timeZone));
  const [beforeEndDate, setBeforeEndDate] = useState(moment(end, 'X').tz(timeZone));
  const [startFocusCount, setFocusCount] = useState(0);
  const [endFocusCount, setEndFocusCount] = useState(0);
  const diabledTime = useCallback(
    (time, date, range) =>
      (range?.[0] &&
        date?.format('YYYY/MM/DD') === range?.[0]?.format('YYYY/MM/DD') &&
        !noLessThan(time, range?.[0]?.format('HH:mm'))) ||
      (range?.[1] &&
        date?.format('YYYY/MM/DD') === range?.[1]?.format('YYYY/MM/DD') &&
        !noLessThan(range?.[1]?.format('HH:mm'), time)),
    [],
  );
  // 超过范围的禁用 [startDateRange[0], startDateRange[1]]
  const startTimeOptions = useMemo(
    () =>
      timeSelectOptions.map((item) => ({
        value: item,
        disabled: diabledTime(item, startDate, startDateRange),
      })),
    [startDate, startDateRange],
  );

  // 超过范围的禁用 (endDateRange[0], endDateRange[1]]
  const endTimeOptions = useMemo(
    () =>
      timeSelectOptions.map((item) => ({
        value: item,
        disabled: diabledTime(item, endDate, endDateRange),
      })),
    [endDate, endDateRange],
  );

  // 选择日期时间
  const handleSelectDateTime = useCallback(
    (value, start: 'start' | 'end', type: 'date' | 'time') => {
      if (start === 'start') {
        let newDate =
          type === 'date'
            ? moment(startDate).year(value.year()).month(value.month()).date(value.date())
            : moment(startDate)
                .hours(+value.split(':')[0])
                .minutes(+value.split(':')[1])
                .seconds(0);
        // 如果当前切换开始时间大于开始时间限制则自动回到最大限制时间
        if (startDateRange?.[1] && newDate > startDateRange?.[1]) {
          newDate = moment(startDateRange?.[1]).set({
            minutes: startDateRange?.[1]?.minutes() >= 30 ? 30 : 0,
            seconds: 0,
          });
        }
        const newEndDate = newDate >= endDate ? moment(newDate).add(120, 'minute') : endDate;
        onChange?.(newDate, newEndDate);
      } else {
        let newDate =
          type === 'date'
            ? moment(endDate).year(value.year()).month(value.month()).date(value.date())
            : moment(endDate).hours(+value.split(':')[0]).minutes(+value.split(':')[1]).seconds(0);
        // 如果当前切换结束时间大于结束时间限制则自动回到最大限制时间
        if (endDateRange?.[1] && newDate > endDateRange?.[1]) {
          newDate = moment(endDateRange?.[1]).set({
            minutes: endDateRange?.[1]?.minutes() >= 30 ? 30 : 0,
            seconds: 0,
          });
        }
        onChange?.(startDate, newDate);
      }
    },
    [startDate, startDateRange, endDate, endDateRange, onChange],
  );

  // 手动输入时间
  const handleTimeInput = useCallback(
    (hour = '0', minute = '0', type) => {
      const regx = /^\d*$/;
      if (!regx.test(hour) || !regx.test(minute)) {
        return;
      }
      if (Number(hour) > 23 || Number(hour) < 0) {
        return;
      }
      if (Number(minute) > 59 || Number(minute) < 0) {
        return;
      }
      handleSelectDateTime(`${hour}:${minute}`, type, 'time');
    },
    [handleSelectDateTime],
  );
  // 手动输入时间聚焦事件
  const timeInputFocus = useCallback(
    (type) => {
      if ((type === 'start' && startFocusCount > 0) || (type === 'end' && endFocusCount > 0))
        return;
      if (type === 'start') {
        setFocusCount(1);
        setBeforeStartDate(startDate);
      } else {
        setEndFocusCount(1);
        setBeforeEndDate(endDate);
      }
    },
    [startDate, endDate, endFocusCount, startFocusCount],
  );
  // 手动输入时间失焦事件
  // 新增时间输入范围校验
  const timeInputBlur = (type: string) => {
    if ((type === 'start' && startFocusCount < 1) || (type === 'end' && endFocusCount < 1)) return;
    if (type === 'start') {
      setFocusCount(0);
      const disabled = diabledTime(startDate.format('HH:mm'), beforeStartDate, startDateRange);
      if (disabled) onChange?.(beforeStartDate, endDate);
    } else {
      setEndFocusCount(0);
      const disabled = diabledTime(endDate.format('HH:mm'), beforeEndDate, endDateRange);
      if (disabled) onChange?.(startDate, beforeEndDate);
    }
  };

  const customizeDatePicker = useCallback(
    (type, isOpen, setIsOpen, disabled) => {
      const date = type === 'start' ? startDate : endDate;
      const range: any = type === 'start' ? startDateRange : endDateRange;
      return (
        <DatePicker
          className={classNames({
            'is-expanded': isOpen,
          })}
          value={date}
          onChange={(value) => handleSelectDateTime(value, type, 'date')}
          range={range}
          onOpenChange={(open) => setIsOpen(open)}
          disabled={disabled}
        />
      );
    },
    [startDate, endDate, handleSelectDateTime, startDateRange, endDateRange],
  );

  const customizeTimePicker = useCallback(
    (type, isOpen, setIsOpen, disabled) => {
      const date = type === 'start' ? startDate : endDate;
      const options = type === 'start' ? startTimeOptions : endTimeOptions;
      const timeRef = useRef(null);
      const { listen } = useOutsideClick(timeRef);
      listen(() => timeInputBlur(type));
      return (
        <div
          className={classNames(`${prefixCls}__time`, {
            'is-expanded': isOpen,
            'is-disabled': disabled,
          })}
          onFocus={() => timeInputFocus(type)}
        >
          <div className={`${prefixCls}__time-input`} ref={timeRef}>
            <input
              value={addNumberPerfix(date.hours())}
              onClick={(e) => setPosition(e.target, 0, -1)}
              onChange={(e) => handleTimeInput(e.target.value, date.minutes().toString(), type)}
              disabled={disabled}
            />
            <span>:</span>
            <input
              value={addNumberPerfix(date.minutes())}
              onClick={(e) => setPosition(e.target, 0, -1)}
              onChange={(e) => handleTimeInput(date.hours().toString(), e.target.value, type)}
              disabled={disabled}
            />
          </div>
          <Select
            appearance="button"
            size="full"
            options={options}
            value={getTimeOfNearHalfHour(date)}
            onChange={(value) => handleSelectDateTime(value, type, 'time')}
            placeholder="请选择"
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            disabled={disabled}
            // @ts-ignore
            // 阻止select框按下回车后自动展开或者收起
            onKeyDown={() => false}
          />
        </div>
      );
    },
    [
      startDate,
      endDate,
      handleTimeInput,
      handleSelectDateTime,
      endTimeOptions,
      startTimeOptions,
      timeInputBlur,
      timeInputFocus,
    ],
  );

  return (
    <div className={classNames(prefixCls, 'size-full-width', className)}>
      <div className={`${prefixCls}__cell ${prefixCls}__cell-start`}>
        {customizeDatePicker('start', isStartDateOpen, setIsStartDateOpen, startDisabled)}
        {customizeTimePicker('start', isStartTimeOpen, setIsStartTimeOpen, startDisabled)}
      </div>
      {!noSplit && <div className={`${prefixCls}__split`}></div>}
      <div className={`${prefixCls}__cell ${prefixCls}__cell-end`}>
        {customizeDatePicker('end', isEndDateOpen, setIsEndDateOpen, endDisabled)}
        {customizeTimePicker('end', isEndTimeOpen, setIsEndTimeOpen, endDisabled)}
      </div>
    </div>
  );
};

export default DateTimePicker;
